/**
 * Turn the assistant's contact sentence into something you can click.
 *
 * Every refusal the assistant makes ends by pointing somewhere useful — that
 * is a rule in the system prompt, and the eval gate fails an answer that
 * refuses without offering the address. It works, and it reads like this:
 *
 *     I don't have information on that. You can also contact him
 *     directly at iftikhar.aiwork@gmail.com.
 *
 * Which is a dead end in a chat window. Nobody selects an address out of a
 * message bubble and pastes it into a mail client; a recruiter who wanted to
 * get in touch has just been handed homework.
 *
 * **The model's text is not changed — only what is drawn.** The sentence
 * carrying the address is lifted out of the prose and replaced with two
 * buttons. The backend still emits it, still stores it, and the eval grader
 * still sees it, so the guarantee that a refusal offers a way through is
 * unaffected: this is a rendering decision, not a prompt change.
 */

import { LinkedInIcon, MailIcon } from "../Icons";

/** The address the prompt and guardrails both use (guardrails.py CONTACT_EMAIL). */
export const CONTACT_EMAIL = "iftikhar.aiwork@gmail.com";

/** Any address, so a change on the backend cannot leave a bare string on screen. */
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/;

/**
 * Split an answer into the part worth reading and the part worth clicking.
 *
 * Sentence-level rather than replacing the address inline: "…at
 * <button>email</button>." reads as a broken sentence, and the surrounding
 * words ("reaches Iftikhar directly", "if you'd rather not wait") only exist to
 * introduce an address that is about to become a labelled button anyway.
 */
export function splitContact(text: string): { prose: string; hasContact: boolean } {
  if (!EMAIL_PATTERN.test(text)) return { prose: text, hasContact: false };

  const sentences = text.split(/(?<=[.!?])\s+/);
  const prose = sentences
    .filter((sentence) => !EMAIL_PATTERN.test(sentence))
    .join(" ")
    .trim()
    // A sentence dropped from the middle can leave a dangling connector.
    .replace(/[\s,;:—-]+$/, "");

  return { prose, hasContact: true };
}

const LINK =
  "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-2xs " +
  "transition-colors duration-200";

export function ContactActions({ linkedinUrl }: { linkedinUrl?: string | null }) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-2">
      <a
        href={`mailto:${CONTACT_EMAIL}`}
        className={`${LINK} border-accent-dim text-accent hover:bg-accent/10`}
      >
        <MailIcon className="size-3.5" />
        Email Iftikhar
      </a>

      {/* Only when the profile actually has one. A LinkedIn button that goes
          nowhere is worse than no button.

          Styled identically to the email one rather than as a muted secondary.
          Dimmed, it read as disabled — the greyed-out button nobody clicks —
          which is the opposite of the intent: these are two equally good ways
          to reach him, and the visitor should pick the one they prefer, not
          the one that looks enabled. */}
      {linkedinUrl && (
        <a
          href={linkedinUrl}
          target="_blank"
          rel="noreferrer"
          className={`${LINK} border-accent-dim text-accent hover:bg-accent/10`}
        >
          <LinkedInIcon className="size-3.5" />
          Message on LinkedIn
        </a>
      )}
    </div>
  );
}
